import { Drawer } from './js-drawer.js';
import { animate as animate4 } from './js-vendor.min.js';

const CartDrawer = class extends Drawer {
	constructor() {
		super();
		this._onPrepareBundledSectionsListener =
			this._onPrepareBundledSections.bind(this);
		this._onCartChangedListener = this._onCartChanged.bind(this);
		this._onCartRefreshListener = this._onCartRefresh.bind(this);
		this._onVariantAddedListener = this._onVariantAdded.bind(this);
		this._refreshPromise = null;
		this._lastLineItemsUpdateAt = 0;
		this._resyncTimer = null;
	}
	_scheduleResync(delayMs = 0, forceLineItems = false) {
		if (this._resyncTimer) {
			window.clearTimeout(this._resyncTimer);
			this._resyncTimer = null;
		}

		this._resyncTimer = window.setTimeout(() => {
			this._resyncTimer = null;
			this._onCartRefresh(forceLineItems).catch((error) => {
				console.error('Failed to resync cart drawer:', error);
			});
		}, delayMs);
	}
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener(
			'cart:prepare-bundled-sections',
			this._onPrepareBundledSectionsListener,
		);
		document.addEventListener('cart:change', this._onCartChangedListener);
		document.addEventListener('cart:refresh', this._onCartRefreshListener);
		document.addEventListener('variant:add', this._onVariantAddedListener);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener(
			'cart:prepare-bundled-sections',
			this._onPrepareBundledSectionsListener,
		);
		document.removeEventListener('cart:change', this._onCartChangedListener);
		document.removeEventListener('cart:refresh', this._onCartRefreshListener);
		document.removeEventListener('variant:add', this._onVariantAddedListener);
	}
	get shouldAppendToBody() {
		return false;
	}
	get openFrom() {
		return 'right';
	}
	async show() {
		// Ensure we always show the latest cart state (including third‑party free gifts)
		const result = await super.show();

		// Single background refresh to sync from server (e.g. Bogos free gifts)
		this._scheduleResync(0, false);

		return result;
	}
	_onPrepareBundledSections(event) {
		event.detail.sections.push(extractSectionId(this));
	}
	/**
	 * Update the cart drawer content when cart content changes.
	 */
	async _onCartChanged(event) {
		if (this._refreshPromise) {
			return;
		}

		const sectionId = extractSectionId(this);
		const sectionContent = event?.detail?.cart?.sections?.[sectionId];

		// Some third-party cart mutations (e.g. Bogos) dispatch events without bundled section HTML.
		// In that case, fall back to a server refresh to keep the drawer in sync.
		if (!sectionContent) {
			// Still schedule follow-up resyncs for line-item changes, because Bogos often performs
			// additional async mutations (gift add/remove) after the initial /cart/change.js response.
			if (event?.detail?.baseEvent === 'line-item:change') {
				this._scheduleResync(2500, true);
			}
			await this._onCartRefresh(true);
			return;
		}

		const updatedDrawerContent = new DOMParser().parseFromString(
			sectionContent,
			'text/html',
		);
		if (event.detail.cart['item_count'] > 0) {
			const currentLineItemsElement = this.querySelector(
					'.cart-drawer__line-items',
				),
				updatedLineItemsElement = updatedDrawerContent.querySelector(
					'.cart-drawer__line-items',
				);
			if (!currentLineItemsElement) {
				this.replaceChildren(
					document
						.createRange()
						.createContextualFragment(
							updatedDrawerContent.querySelector('.cart-drawer').innerHTML,
						),
				);
			} else {
				const delay =
					event.detail.baseEvent === 'variant:add' ||
					event.detail.baseEvent === 'line-item:change'
						? 0
						: 1250;
				setTimeout(() => {
					currentLineItemsElement.innerHTML = updatedLineItemsElement.innerHTML;
					this._lastLineItemsUpdateAt = Date.now();
				}, delay);
				this.querySelector('[slot="footer"]').replaceChildren(
					...updatedDrawerContent.querySelector('[slot="footer"]').childNodes,
				);
				// Refresh secondary column (recommendations, BXGY panels, etc.)
				const currentSecondary = this.querySelector('.cart-drawer__secondary');
				const updatedSecondary = updatedDrawerContent.querySelector(
					'.cart-drawer__secondary',
				);
				if (currentSecondary && updatedSecondary) {
					currentSecondary.replaceChildren(...updatedSecondary.childNodes);
				}
				// Signal that cart drawer content has been updated so listeners can re-initialize
				document.documentElement.dispatchEvent(
					new CustomEvent('cart:updated', { bubbles: true }),
				);

				// When a line-item is changed, third-party apps like Bogos may perform additional
				// cart mutations (e.g. removing free gifts) shortly afterwards. Schedule a single
				// follow-up refresh to pick up those changes without requiring a full page reload.
				if (event.detail.baseEvent === 'line-item:change') {
					// Wait for Bogos' secondary mutation (gift add/remove) before re-syncing.
					this._scheduleResync(2500, true);
				}
			}
		} else {
			await animate4(this.children, { opacity: 0 }, { duration: 0.15 })
				.finished;
			this.replaceChildren(
				...updatedDrawerContent.querySelector('.cart-drawer').childNodes,
			);
			animate4(
				this.querySelector('.empty-state'),
				{ opacity: [0, 1], transform: ['translateY(20px)', 'translateY(0)'] },
				{ duration: 0.15 },
			);
			// Signal that cart drawer content has been updated so listeners can re-initialize
			document.documentElement.dispatchEvent(
				new CustomEvent('cart:updated', { bubbles: true }),
			);
		}
	}
	/**
	 * Listeners called when a new variant has been added
	 */
	_onVariantAdded(event) {
		if (
			window.themeVariables.settings.cartType !== 'drawer' ||
			event.detail?.blockCartDrawerOpening
		) {
			return;
		}
		this.show();

		// Bogos often adds/removes gifts asynchronously after the initial cart mutation.
		// Schedule a single follow-up resync to pick up late app changes.
		this._scheduleResync(2500, true);
	}
	/**
	 * Force a complete refresh of the cart drawer (this is called by dispatching the 'cart:refresh' on the document)
	 */
	async _onCartRefresh(eventOrForceLineItems = false) {
		const forceLineItems = eventOrForceLineItems === true;
		if (this._refreshPromise) return this._refreshPromise;

		this._refreshPromise = (async () => {
			const tempDiv = document.createElement('div');
			const sectionId = extractSectionId(this);
			// Cache-buster helps avoid briefly stale section HTML after async app cart mutations.
			const url = `${window.Shopify.routes.root}?section_id=${sectionId}&t=${Date.now()}`;
			tempDiv.innerHTML = await (await fetch(url)).text();
			const updatedDrawerRoot = tempDiv.querySelector('#cart-drawer');
			if (!updatedDrawerRoot) return;

			// Targeted update (mirror _onCartChanged) to avoid re-creating the whole drawer DOM,
			// which reduces image "flashing" when the cart is refreshed.
			const updatedLineItemsElement = updatedDrawerRoot.querySelector(
				'.cart-drawer__line-items',
			);
			const currentLineItemsElement = this.querySelector(
				'.cart-drawer__line-items',
			);
			const updatedPrimary = updatedDrawerRoot.querySelector(
				'.cart-drawer__primary',
			);
			const currentPrimary = this.querySelector('.cart-drawer__primary');
			// Avoid a second rapid DOM swap if we just updated from cart:change,
			// which is what causes the double "flash" on line-item__media-wrapper.
			const recentlyUpdated =
				!forceLineItems &&
				this._lastLineItemsUpdateAt &&
				Date.now() - this._lastLineItemsUpdateAt < 500;

			// Handle empty <-> non-empty transitions (when .cart-drawer__line-items may not exist)
			if (
				(!currentLineItemsElement && updatedLineItemsElement) ||
				(currentLineItemsElement && !updatedLineItemsElement)
			) {
				if (currentPrimary && updatedPrimary) {
					currentPrimary.replaceChildren(...updatedPrimary.childNodes);
					this._lastLineItemsUpdateAt = Date.now();
				} else {
					// Fallback: replace whole drawer content if primary columns aren't found.
					this.replaceChildren(...updatedDrawerRoot.childNodes);
					this._lastLineItemsUpdateAt = Date.now();
				}
			} else if (
				currentLineItemsElement &&
				updatedLineItemsElement &&
				!recentlyUpdated
			) {
				const nextHtml = updatedLineItemsElement.innerHTML;
				if (currentLineItemsElement.innerHTML !== nextHtml) {
					currentLineItemsElement.innerHTML = nextHtml;
					this._lastLineItemsUpdateAt = Date.now();
				}
			}

			const updatedFooter = updatedDrawerRoot.querySelector('[slot="footer"]');
			const currentFooter = this.querySelector('[slot="footer"]');
			if (currentFooter && updatedFooter) {
				currentFooter.replaceChildren(...updatedFooter.childNodes);
			}

			// Signal that cart drawer content has been updated so listeners can re-initialize (e.g., native recommendations)
			document.documentElement.dispatchEvent(
				new CustomEvent('cart:updated', { bubbles: true }),
			);
		})();

		try {
			return await this._refreshPromise;
		} finally {
			this._refreshPromise = null;
		}
	}
};

const CartNotificationDrawer = class extends Drawer {
	constructor() {
		super();
		this._onVariantAddedListener = this._onVariantAdded.bind(this);
	}
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener('variant:add', this._onVariantAddedListener);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener('variant:add', this._onVariantAddedListener);
	}
	async show() {
		this.style.display = 'block';
		return super.show();
	}
	async hide() {
		super.hide()?.then(() => {
			this.style.display = 'none';
		});
	}
	_onVariantAdded(event) {
		if (
			window.themeVariables.settings.cartType !== 'popover' ||
			event.detail?.blockCartDrawerOpening
		) {
			return;
		}
		const tempContent = document.createElement('div');
		tempContent.innerHTML = event.detail.cart.sections['variant-added'];
		this.replaceChildren(
			...tempContent.querySelector('.shopify-section').children,
		);
		this.show();
	}
};
const LineItem = class extends HTMLElement {
  connectedCallback() {
    this.pillLoaderElement = this.querySelector("pill-loader");
    this.addEventListener("line-item:will-change", this._onWillChange.bind(this));
    this.addEventListener("line-item:change", this._onChanged.bind(this));
  }
  _onWillChange() {
    this.pillLoaderElement?.setAttribute("aria-busy", "true");
  }
  async _onChanged(event) {
    this.pillLoaderElement?.removeAttribute("aria-busy");
    if (event.detail.cart["item_count"] === 0 || event.detail.quantity !== 0) {
      return;
    }
    let marginCompensation = 0;
    if (this.nextElementSibling) {
      marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
    }
    await animate4(this, { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: "hidden", opacity: [1, 0] }, { duration: 0.2, easing: "ease" }).finished;
    this.remove();
  }
};
if (!window.customElements.get('cart-drawer')) {
	window.customElements.define('cart-drawer', CartDrawer);
}
if (!window.customElements.get('cart-notification-drawer')) {
	window.customElements.define(
		'cart-notification-drawer',
		CartNotificationDrawer,
	);
}
if (!window.customElements.get('line-item')) {
	window.customElements.define('line-item', LineItem);
}

function extractSectionId(element) {
	const sectionElement = element.classList.contains('shopify-section')
		? element
		: element.closest('.shopify-section');
	return sectionElement.id.replace('shopify-section-', '');
}

const ProductForm = class extends HTMLFormElement {
	constructor() {
		super();
		if (
			window.themeVariables.settings.cartType !== 'page' &&
			window.themeVariables.settings.pageType !== 'cart'
		) {
			this.addEventListener('submit', this._onSubmit);
		}
	}
	connectedCallback() {
		// Enable submit buttons when form is connected
		const submitButtons = Array.from(this.elements).filter(
			(button) => button.type === 'submit',
		);
		for (const submitButton of submitButtons) {
			submitButton.removeAttribute('disabled');
		}
	}
	async _onSubmit(event) {
		event.preventDefault();
		if (!this.checkValidity()) {
			this.reportValidity();
			return;
		}
		const submitButtons = Array.from(this.elements).filter(
			(button) => button.type === 'submit',
		);
		for (const submitButton of submitButtons) {
			submitButton.setAttribute('disabled', 'disabled');
			submitButton.setAttribute('aria-busy', 'true');
		}
		const sectionsToBundle = ['variant-added'];
		document.documentElement.dispatchEvent(
			new CustomEvent('cart:prepare-bundled-sections', {
				bubbles: true,
				detail: { sections: sectionsToBundle },
			}),
		);
		const formData = new FormData(this);
		const variantId = formData.get('id');

		// Validate that variant ID exists
		if (!variantId) {
			console.error('ProductForm: No variant ID found in form');
			for (const submitButton of submitButtons) {
				submitButton.removeAttribute('disabled');
				submitButton.removeAttribute('aria-busy');
			}
			return;
		}

		formData.set('sections', sectionsToBundle.join(','));
		formData.set('sections_url', `${Shopify.routes.root}variants/${variantId}`);

		const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
			body: formData,
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
				// Needed for Shopify to check inventory
			},
		});
		for (const submitButton of submitButtons) {
			submitButton.removeAttribute('disabled');
			submitButton.removeAttribute('aria-busy');
		}

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			console.error('ProductForm: Failed to add to cart', errorData);
			if (errorData.description) {
				alert(errorData.description);
			}
			this.dispatchEvent(
				new CustomEvent('cart:error', {
					bubbles: true,
					detail: {
						error: errorData.description || 'Failed to add to cart',
					},
				}),
			);
			return;
		}

		const responseJson = await response.json();
		const cartContent = await (
			await fetch(`${Shopify.routes.root}cart.js`)
		).json();
		cartContent.sections = responseJson.sections;
		this.dispatchEvent(
			new CustomEvent('variant:add', {
				bubbles: true,
				detail: {
					items: Object.prototype.hasOwnProperty.call(responseJson, 'items')
						? responseJson.items
						: [responseJson],
					cart: cartContent,
				},
			}),
		);
		document.documentElement.dispatchEvent(
			new CustomEvent('cart:change', {
				bubbles: true,
				detail: {
					baseEvent: 'variant:add',
					cart: cartContent,
				},
			}),
		);
	}
};
if (!window.customElements.get('product-form')) {
	window.customElements.define('product-form', ProductForm, {
		extends: 'form',
	});
}

// js/common/form/quantity-selector.js
const selectors = {
	comparePrice: '.js-compare-price',
	salePrice: '.js-sale-price',
	regularPrice: '.js-regular-price',
	formattedPrice: '.js-formatted-price',
};
const QuantitySelector = class extends HTMLElement {
	connectedCallback() {
		this._abortController = new AbortController();
		this.inputElement = this.querySelector('input');

		this.querySelector('button:first-of-type').addEventListener(
			'click',
			() => {
				this.inputElement.quantity = this.inputElement.quantity - 1;
				this.inputElement._updatePrice(this.inputElement.quantity);
				this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
			},
			{ signal: this._abortController.signal },
		);

		this.querySelector('button:last-of-type').addEventListener(
			'click',
			() => {
				this.inputElement.quantity = this.inputElement.quantity + 1;
				this.inputElement._updatePrice(this.inputElement.quantity);
				this.inputElement.dispatchEvent(new Event('change', { bubbles: true }));
			},
			{ signal: this._abortController.signal },
		);
	}
	disconnectedCallback() {
		this._abortController.abort();
	}
};
const QuantityInput = class extends HTMLInputElement {
	constructor() {
		super();
		this._abortController = new AbortController();
		this.addEventListener('input', this._onValueInput, {
			signal: this._abortController.signal,
		});
		this.addEventListener('change', this._onValueChanged, {
			signal: this._abortController.signal,
		});
		this.addEventListener('keydown', this._onKeyDown, {
			signal: this._abortController.signal,
		});
		this.addEventListener('focus', this.select, {
			signal: this._abortController.signal,
		});
	}
	disconnectedCallback() {
		this._abortController?.abort();
	}
	get quantity() {
		return Number.parseInt(this.value);
	}
	set quantity(quantity) {
		const isNumeric =
			(typeof quantity === 'number' ||
				(typeof quantity === 'string' && quantity.trim() !== '')) &&
			!Number.isNaN(quantity);
		if (quantity === '') {
			return;
		}
		let parsedQuantity = quantity;
		if (!isNumeric || quantity < 0) {
			parsedQuantity = Number.parseInt(quantity) || 1;
		}
		this.value = Math.max(
			this.min || 1,
			Math.min(parsedQuantity, this.max || Number.MAX_VALUE),
		).toString();
		this.size = Math.max(this.value.length + 1, 2);
	}
	_onValueInput() {
		this.quantity = this.value;
		this._updatePrice(this.quantity);
	}
	_onValueChanged() {
		if (this.value === '') {
			this.quantity = 1;
		}
		this._updatePrice(this.quantity);
	}
	_onKeyDown(event) {
		event.stopPropagation();
		const originalQuantity = this.quantity;
		if (event.key === 'ArrowUp') {
			this.quantity = this.quantity + 1;
		} else if (event.key === 'ArrowDown') {
			this.quantity = this.quantity - 1;
		}
		if (originalQuantity !== this.quantity) {
			this._updatePrice(this.quantity);
			this._abortController = new AbortController();
			this.addEventListener(
				'blur',
				() => this.dispatchEvent(new Event('change', { bubbles: true })),
				{ once: true, signal: this._abortController.signal },
			);
		}
	}

	_updatePrice(qty) {
		const container = this.closest('.product-info__quantity-selector');
		if (!container) return;

		const priceElements = {
			sale: container.querySelector(selectors.salePrice),
			regular: container.querySelector(selectors.regularPrice),
			compare: container.querySelector(selectors.comparePrice),
		};

		if (!priceElements.sale && !priceElements.regular) return;

		const priceBox = container.querySelector('.js-price-list');
		const format = (cents) => {
			const moneyFormat = priceBox?.dataset.moneyFormat;
			if (window?.Shopify?.formatMoney && moneyFormat) {
				return window.Shopify.formatMoney(cents, moneyFormat);
			}
			const currency =
				priceBox?.dataset.currency || window.Shopify?.currency || 'GBP';
			const locale = document.documentElement.lang || undefined;
			try {
				return new Intl.NumberFormat(locale, {
					style: 'currency',
					currency,
				}).format(cents / 100);
			} catch (_) {
				return (cents / 100).toFixed(2);
			}
		};

		for (const [type, element] of Object.entries(priceElements)) {
			if (!element) continue;

			const unitCents = Number(element.dataset.price || 0);
			const totalCents = unitCents * qty;
			const outputElement = element.querySelector(selectors.formattedPrice);

			if (outputElement && (type !== 'compare' || unitCents > 0)) {
				outputElement.innerHTML = format(totalCents);
			}
		}
	}
};
if (!window.customElements.get('quantity-selector')) {
	window.customElements.define('quantity-selector', QuantitySelector);
}
if (!window.customElements.get('quantity-input')) {
	window.customElements.define('quantity-input', QuantityInput, {
		extends: 'input',
	});
}
